import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FileText, Plus } from 'lucide-react';
import ContentItemCard from '@/components/content/ContentItemCard';
import ContentCreateDialog from '@/components/content/ContentCreateDialog';
import SubmissionsDialog from '@/components/shared/SubmissionsDialog';
import EditScheduleDialog from '@/components/shared/EditScheduleDialog';
import ResourceReuseModal from '@/components/shared/ResourceReuseModal';
import StudentCorrectionsSection from '@/components/StudentCorrectionsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContentTab({
  classId, contentType,
  uploadLabel, downloadLabel, submitLabel, reviewLabel,
  showExternalCompletion, classMembers = []
}) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const isStudent = user?.role === 'student';

  const [items, setItems] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [editScheduleItem, setEditScheduleItem] = useState(null);
  const [reuseItem, setReuseItem] = useState(null);

  const fetchClassStudents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes/${classId}`, headers);
      setClassStudents((res.data.members || []).filter(m => m.role === 'student'));
    } catch (err) { console.error("[ContentTab] silent error:", err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, token]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/content/list/${classId}/${contentType}`, headers);
      setItems(res.data);
    } catch (err) { console.error("[ContentTab] silent error:", err); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, contentType, token]);

  useEffect(() => { fetchItems(); fetchClassStudents(); }, [fetchItems, fetchClassStudents]);

  const handleItemUpdated = (id, patch) => {
    if (patch) {
      setItems(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
    } else {
      fetchItems();
    }
  };

  const handleDelete = async (contentId) => {
    try {
      await axios.delete(`${API}/content/delete/${contentId}`, headers);
      toast.success(t('success'));
      fetchItems();
    } catch { toast.error(t('error')); }
  };

  const handleReview = async (contentId) => {
    try {
      const res = await axios.get(`${API}/content/submissions/${contentId}`, headers);
      setSubmissions(res.data);
      setReviewingId(contentId);
    } catch { toast.error(t('error')); }
  };

  const handleUpdateSchedule = async (scheduled_for) => {
    if (!editScheduleItem) return;
    try {
      await axios.put(`${API}/content/update/${editScheduleItem.id}`, { scheduled_for }, headers);
      toast.success(t('schedule_updated'));
      setEditScheduleItem(null);
      fetchItems();
    } catch { toast.error(t('error')); }
  };

  return (
    <div data-testid={`${contentType}-tab`}>
      {/* Fase 2 · Sección superior de correcciones Feedback Hub. Solo visible
          para el alumno y solo dentro de la pestaña «Correcciones». No
          reemplaza ni altera el listado legacy que renderiza más abajo. */}
      {isStudent && contentType === 'correction' && (
        <StudentCorrectionsSection classId={classId} token={token} />
      )}
      {/* Fase 2 · The residual legacy header («Descargar corrección») is
          hidden for students inside the «Correcciones» tab because the FH
          section above already introduces the list, and there is no legacy
          download flow left for students on this tab. The header stays
          visible for admin/docente (they still use the upload flow). */}
      <div
        className="flex items-center justify-between mb-6"
        style={{ display: (isStudent && contentType === 'correction') ? 'none' : undefined }}
      >
        <h3 className="text-lg font-bold text-slate-700" style={{ fontFamily: 'Montserrat' }}>
          {isStudent ? downloadLabel : uploadLabel}
        </h3>
        {!isStudent && (
          <Button data-testid={`create-${contentType}-btn`} onClick={() => setShowCreate(true)} size="sm"
            className="gap-1 rounded-lg"
            style={{ backgroundColor: contentType === 'task' ? '#F59E0B' : contentType === 'correction' ? '#10B981' : '#8B5CF6' }}>
            <Plus className="w-4 h-4" /> {t('create')}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>{t('no_items')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <ContentItemCard
              key={item.id}
              item={item}
              contentType={contentType}
              isStudent={isStudent}
              token={token}
              t={t}
              downloadLabel={downloadLabel}
              submitLabel={submitLabel}
              showExternalCompletion={showExternalCompletion}
              reviewLabel={reviewLabel}
              onUpdated={handleItemUpdated}
              onOpenSchedule={setEditScheduleItem}
              onReview={handleReview}
              onDelete={handleDelete}
              onReuse={setReuseItem}
              classStudents={classStudents}
            />
          ))}
        </div>
      )}

      <ContentCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        classId={classId}
        contentType={contentType}
        uploadLabel={uploadLabel}
        classStudents={classStudents}
        isStudent={isStudent}
        token={token}
        t={t}
        onCreated={fetchItems}
      />

      <SubmissionsDialog
        open={!!reviewingId}
        onClose={() => setReviewingId(null)}
        title={reviewLabel}
        submissions={submissions}
        token={token}
        t={t}
        contentId={reviewingId}
      />

      <EditScheduleDialog
        item={editScheduleItem}
        onClose={() => setEditScheduleItem(null)}
        onSave={handleUpdateSchedule}
        t={t}
      />

      <ResourceReuseModal
        open={!!reuseItem}
        onClose={() => setReuseItem(null)}
        resourceTitle={reuseItem?.title}
        contentId={reuseItem?.id}
        token={token}
        scheduledAssignments={reuseItem?.scheduled_assignments || []}
        classStudents={classStudents}
        currentAssignedTo={reuseItem?.assigned_to}
        onAssign={async (ids, scheduledFor) => {
          const res = await axios.post(`${API}/content/${reuseItem.id}/assign-students`, { student_ids: ids, scheduled_for: scheduledFor }, headers);
          return res.data;
        }}
        onAssigned={fetchItems}
      />
    </div>
  );
}
